import { DialogActions, DialogTitle } from "@material-ui/core";
import React, { Component } from "react";
import styled from "styled-components";

import * as ApiErrors from "apis/errors";
import { DEFAULT_AI_IMAGEMODEL } from "../../../../common/aiConstants";
import { ActionPermissionsObject } from "../../../../common/interfaces/index";
import { SlideCategories } from "../../../../common/SlideCategories/CategoryController";
import { Button } from "../../../Components/Button";
import { Dropdown } from "../../../Components/Dropdown";
import { Icon } from "../../../Components/Icon";
import { MenuItem } from "../../../Components/Menu";
import { ToggleButton, ToggleButtons } from "../../../Components/ToggleButtons";
import { ToggleSwitch } from "../../../Components/ToggleSwitch";
import { WithLabel } from "../../../Components/WithLabel";
import WorkspaceController from "../../../controllers/WorkspaceController";
import getLogger, { LogGroup } from "../../../core/logger";
import { FileType, aiContextFileTypes, validateFileTypeAndSize } from "../../../core/utilities/fileSourcesAndTypes";
import { Key } from "../../../core/utilities/keys";
import { PropertyPanelContainer, PropertySection } from "../../../EditorComponents/PropertyPanel";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "../../../react/components/Dialogs/BaseDialog";
import { FlexSpacer } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { themeColors } from "../../../react/sharedStyles";
import ImportFileDialog from "../../../react/views/ImportFile/ImportFileDialog";
import { $, _ } from "../../../vendor";
import { AiIcon } from "../AiIcon";
import { AIImageSourcePicker, AiImageStylePopup } from "../GenerateImage/AiImageStyle";
import { AiSlideContentEditor } from "../GeneratePresentation/Components/AiSlideContentEditor";
import FileEntry from "../GeneratePresentation/Components/FileEntry";
import { LanguagePicker } from "../GeneratePresentation/Components/LanguagePicker";
import SlideAiController from "./SlideAiController";
import { ContextFileTooLargeError } from "../../../core/services/aiGeneration";
import Analytics from "../../../analytics";

const logger = getLogger(LogGroup.AI);

const SLIDE_GAP = 20;

const Container = styled.div`
    width: 100%;
    display: grid;
    grid-template-areas: 
"prompt options";
    grid-template-columns: 1fr 300px;
    row-gap: 10px;
    column-gap: 20px;
    background: #f9f9f9;
    padding: 10px 20px 20px;
    overflow-y: auto;
`;

const PromptContainer = styled.div`
    grid-area: prompt;
    display: grid;
    grid-template-rows: auto 1fr;


    &&& {
        .MuiToggleButton-root {
            position: relative;
            top: 1px;
            border: solid 1px #ddd;
        }
        
        .MuiToggleButton-root.Mui-selected {
            background: white;
            color: #333;
            border: solid 1px #ccc;
            border-bottom: solid 1px white;

            .bai-icon {
                color: #333 !important;
            }
        }
    }
`;

const OptionsContainer = styled.div`
    grid-area: options;
    min-height: 0px;
    display: grid;
    grid-template-rows: auto 1fr;
`;

const TextContainer = styled.div`
    background: #fff;
    border: solid 1px #ccc;
    padding: 10px;
    width: 100%;
    min-height: 100px;
    pointer-events: auto;
    overflow-y: auto;
`;

class SlideAiDialog extends Component {
    constructor(props) {
        super(props);

        let metadata = props.currentSlide.getMetadata() ?? {
            title: "Loading..."
        };

        let currentPrompt = SlideAiController.buildPromptFromSlide(props.currentCanvasController.canvas);

        if (currentPrompt.trim().length === 0 && metadata.slidePrompt) {
            currentPrompt = metadata.slidePrompt;
        }

        this.state = {
            currentModel: _.cloneDeep(props.currentSlide.attributes),
            isGenerating: false,
            selectedModel: null,
            textDensity: 1,
            slideCategory: null,
            promptType: metadata.slidePrompt == null ? "current" : "original",
            originalSlidePrompt: metadata.slidePrompt,
            currentSlidePrompt: currentPrompt,
            slidePrompt: metadata.slidePrompt ?? currentPrompt,
            slideType: "auto",
            language: metadata.language || "auto",
            preserveExactText: !!metadata.preserveExactText,
            usePresentationAsContext: true,
            isDragging: false,
            slideWidth: (window.innerWidth - SLIDE_GAP * 4 - 64) / 3 - 10,
            ideas: metadata.generatedSlides || [],
            imageSource: metadata.imageSource || "ai",
            imageStyle: metadata.generatedModel?.slideImage?.imageStyle || "**theme**",
            imageModel: metadata.imageModel || DEFAULT_AI_IMAGEMODEL,
        };

        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        if (this.state.imageSource === "ai" && !canGenerate) {
            this.state.imageSource = "web";
        }

        this.promptRef = React.createRef();
        this.actionsContainerRef = React.createRef();
    }

    componentDidMount() {
        $(window).on("keydown", event => {
            if (event.which == Key.ESCAPE) {
                this.props.closeDialog();
            }
        });
    }

    regenerateSlide = async () => {
        const { closeDialog, currentSlide } = this.props;
        const { slideType, language, preserveExactText, usePresentationAsContext, imageSource, imageStyle, imageModel, webSearch } = this.state;

        this.setState({ isGenerating: true });
        let slidePrompt = this.promptRef.current.getMarkdown();

        closeDialog();

        let props = {
            slideType,
            language,
            preserveExactText,
            usePresentationAsContext,
            imageSource,
            imageStyle,
            imageModel,
            webSearch,
            regenerateSlideImage: true
        };

        await SlideAiController.regenerateIdeas(slidePrompt, props);

        Analytics.AiGeneration.GenerateSlideIdeas({ slide: currentSlide });

        this.setState({ isGenerating: false });
    }

    handleSetPrompt = type => {
        const { originalSlidePrompt, currentSlidePrompt } = this.state;

        if (type === "current") {
            this.setState({ slidePrompt: currentSlidePrompt, promptType: type });
        } else {
            this.setState({ slidePrompt: originalSlidePrompt, promptType: type });
        }
    }

    handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.isDragging) {
            this.setState({ isDragging: true });
        }
    }

    handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();

        this.setState({ isDragging: false });
    }

    handleDrop = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });

        let droppedFiles = Array.from(e.dataTransfer.files);

        for (let file of droppedFiles) {
            const errorMessage = validateFileTypeAndSize(file, aiContextFileTypes);
            if (errorMessage) {
                ShowErrorDialog({
                    title: "File is not supported",
                    message: errorMessage,
                });
                continue;
            }

            await this.processFile(file);
        }
    }

    handleAddFile = () => {
        ShowDialog(ImportFileDialog, {
            fileTypes: aiContextFileTypes,
            closeOnSuccess: true,
            onSuccess: async file => {
                if (file) {
                    this.processFile(file);
                }
            }
        });
    }

    processFile = async file => {
        try {
            await SlideAiController.parseDocument(file);
        } catch (err) {
            logger.error(err, "[SlideAiDialog] processFile() error", { fileName: file.name, fileSize: file.size });

            if (err instanceof ApiErrors.ContentTooLargeError || err instanceof ContextFileTooLargeError) {
                ShowErrorDialog({
                    title: "File is too large",
                    message: err.message,
                });
                return;
            }

            ShowErrorDialog({
                title: `Error processing ${file.name}`,
                message: "Sorry, we couldn't process the file. Please try again with a different file.",
            });
        }
    }

    handleRemoveFile = file => {
        SlideAiController.removeFile(file.name);
    }

    handleClose = () => {
        let { closeDialog } = this.props;
        SlideAiController.resetFiles();
        closeDialog();
    }

    render() {
        let { canvasController, files } = this.props;
        let { isGenerating, promptType, slidePrompt, language, imageSource, imageStyle, imageModel, preserveExactText, usePresentationAsContext, slideType, webSearch, isDragging } = this.state;

        return (
            <BeautifulDialog closeDialog={this.handleClose} maxWidth="lg" onDragOver={this.handleDragOver} onDragLeave={this.handleDragLeave} onDrop={this.handleDrop} ref={this.actionsContainerRef}>
                <DialogTitle>
                    <FlexBox left middle>
                        Generate Slide Ideas with AI
                    </FlexBox>
                </DialogTitle>
                <Container>
                    <PromptContainer>
                        <FlexBox left middle>
                            <SimpleLabel>SLIDE PROMPT</SimpleLabel>
                            <FlexSpacer />
                            <ToggleButtons value={promptType} onChange={value => this.handleSetPrompt(value)}>
                                <ToggleButton value="original">
                                    Use Original Prompt
                                </ToggleButton>
                                <ToggleButton value="current">
                                    Use Current Slide
                                </ToggleButton>
                            </ToggleButtons>
                        </FlexBox>
                        <TextContainer>
                            <AiSlideContentEditor ref={this.promptRef} text={slidePrompt} onChange={slidePrompt => this.setState({ slidePrompt })} t={this.props.t} />
                        </TextContainer>
                    </PromptContainer>
                    <OptionsContainer>
                        <FlexBox left middle>
                            <SimpleLabel>OPTIONS</SimpleLabel>
                        </FlexBox>
                        <PropertyPanelContainer style={{ border: "solid 1px #ccc" }}>
                            <PropertySection>
                                <WithLabel label="Slide Type">
                                    <Dropdown value={slideType} onChange={slideType => this.setState({ slideType })}>
                                        <MenuItem value="auto">Auto Detect</MenuItem>
                                        {Object.keys(SlideCategories).map(key => (
                                            <MenuItem key={key} value={SlideCategories[key]}>{_.startCase(_.toLower(SlideCategories[key]))}</MenuItem>
                                        ))}
                                    </Dropdown>
                                </WithLabel>
                                <WithLabel label="Language">
                                    <LanguagePicker value={language} onChange={language => this.setState({ language })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Use Presentation Context" toolTip="Generate this slide using the context of the entire presentation.">
                                    <ToggleSwitch value={usePresentationAsContext} onChange={usePresentationAsContext => this.setState({ usePresentationAsContext })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Preserve Exact Text" toolTip="Try to keep the text in the generated slides exactly as it appears in the prompt.">
                                    <ToggleSwitch value={preserveExactText} onChange={preserveExactText => this.setState({ preserveExactText })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel label="Image Source">
                                    <AIImageSourcePicker value={imageSource} onChange={imageSource => this.setState({ imageSource })} />
                                </WithLabel>
                                {imageSource == "ai" && (
                                    <WithLabel label="Ai Image Style" left>
                                        <AiImageStylePopup theme={canvasController.canvas.getTheme()} imageStyle={imageStyle} imageModel={imageModel} onChange={(imageStyle, imageModel) => this.setState({ imageStyle, imageModel })} />
                                    </WithLabel>
                                )}
                            </PropertySection>
                        </PropertyPanelContainer>
                    </OptionsContainer>
                </Container>
                <DialogActions>
                    <FlexBox vertical gap={10} fillWidth>
                        <FlexBox horizontalAlign="between" gap={20} fillWidth>
                            <FlexBox left middle gap={20}>
                                <WithLabel right tight label="Enable Web Search">
                                    <ToggleSwitch value={webSearch} onChange={webSearch => this.setState({ webSearch })} />
                                </WithLabel>
                                <Button unframed onClick={this.handleAddFile}>
                                    <Icon>{isDragging ? "check_circle" : "add_circle"}</Icon>
                                    {isDragging ? "Drop files here" : "Add file..."}
                                </Button>
                            </FlexBox>
                            <FlexSpacer />
                            <Button unframed onClick={this.handleClose}>Cancel</Button>
                            <Button large height={45} color={themeColors.aiColor}
                                onClick={this.regenerateSlide}
                                showSpinner={isGenerating}
                                disabled={isGenerating || _.isEmpty(slidePrompt)}>
                                <AiIcon />
                                Generate Slide Ideas
                            </Button>
                        </FlexBox>
                        {files.length > 0 && (
                            <FlexBox wrap gap={10} horizontalAlign="start" fillWidth>
                                {files.map((file, index) => (
                                    <FileEntry key={index} file={file} onRemove={this.handleRemoveFile} />
                                ))}
                            </FlexBox>
                        )}
                    </FlexBox>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

export default SlideAiController.withState(SlideAiDialog);
